import React from 'react';
import { Button, Flex, Text, TextField } from '@chatsdet/ui';
import { Plus, Trash } from '@phosphor-icons/react';
import { labelToSnakeName, uniqueName } from './nameUtils';

type ActionOutputsEditorProps = {
  declaredOutputs: string[];
  capturedVariableKeys: string[];
  onAddOutput: (name: string) => void;
  onDeleteOutput: (name: string) => void;
  onClearAll: () => void;
};

const helpText = (
  <Text size="sm" className="text-muted-foreground mt-1">
    Declare variables the agent should capture from this step using browser_evaluate with saveAs.
    Captured values become available as <code className="text-xs">$var.name</code> in downstream nodes.
  </Text>
);

function OutputRow({ name, onDelete, dimmed, label }: {
  name: string;
  onDelete: () => void;
  dimmed?: boolean;
  label?: string;
}) {
  return (
    <Flex
      align="center"
      justify="between"
      className={`rounded-md px-3 py-1.5 ${dimmed ? 'bg-muted/20 opacity-60' : 'bg-muted/30'}`}
    >
      <Flex align="center" gap="sm">
        <Text size="sm" className="font-mono">{name}</Text>
        {label && <Text size="xs" className="text-muted-foreground">({label})</Text>}
      </Flex>
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Remove output"
        onPress={onDelete}
        className="text-muted-foreground hover:text-destructive"
      >
        <Trash size={14} />
      </Button>
    </Flex>
  );
}

export const ActionOutputsEditor: React.FC<ActionOutputsEditorProps> = ({
  declaredOutputs,
  capturedVariableKeys,
  onAddOutput,
  onDeleteOutput,
  onClearAll,
}) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const [newName, setNewName] = React.useState('');

  const resetAddForm = React.useCallback(() => {
    setIsAdding(false);
    setNewName('');
  }, []);

  const handleSubmit = React.useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const baseName = labelToSnakeName(trimmed);
    if (!baseName) return;
    const allNames = [...declaredOutputs, ...capturedVariableKeys];
    const name = uniqueName(baseName, allNames);
    onAddOutput(name);
    resetAddForm();
  }, [newName, declaredOutputs, capturedVariableKeys, onAddOutput, resetAddForm]);

  // Auto-captured keys that aren't already in declaredOutputs
  const autoCapturedOnly = capturedVariableKeys.filter(
    (key) => !declaredOutputs.includes(key)
  );

  const hasAny = declaredOutputs.length > 0 || autoCapturedOnly.length > 0;

  const addButton = isAdding ? (
    <div className="mt-3 rounded-md border border-border/60 p-3 space-y-3">
      <TextField
        label="Output name"
        placeholder="e.g. invoice_number"
        value={newName}
        onChange={setNewName}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          } else if (e.key === 'Escape') {
            resetAddForm();
          }
        }}
      />
      <Text size="xs" className="text-muted-foreground">
        Will be converted to snake_case
      </Text>
      <Flex gap="sm" justify="end">
        <Button variant="ghost" size="sm" onPress={resetAddForm}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          isDisabled={!newName.trim() || !labelToSnakeName(newName.trim())}
          onPress={handleSubmit}
        >
          Add
        </Button>
      </Flex>
    </div>
  ) : (
    <div className="mt-3">
      <Button variant="outline" size="sm" iconLeft={<Plus size={14} />} onPress={() => setIsAdding(true)}>
        Add output
      </Button>
    </div>
  );

  return (
    <div>
      {hasAny && (
        <Flex align="center" justify="end" className="mb-2">
          <Button variant="ghost" size="sm" onPress={onClearAll} className="text-muted-foreground">
            Clear all
          </Button>
        </Flex>
      )}
      {helpText}

      {hasAny && (
        <div className="mt-3 space-y-2">
          {declaredOutputs.map((name) => (
            <OutputRow key={name} name={name} onDelete={() => onDeleteOutput(name)} />
          ))}
          {autoCapturedOnly.map((name) => (
            <OutputRow key={name} name={name} onDelete={() => onDeleteOutput(name)} dimmed label="auto-captured" />
          ))}
        </div>
      )}

      {addButton}
    </div>
  );
};
